/**
 * Copy, cut and paste in the graph window, through the system's clipboard (so nodes go from
 * one view, board, page or project to another): the page's `copy`, `cut` and `paste` events
 * while the view is shown and nothing is being typed. What is copied is the selected nodes as
 * JSON text (`state/clipboard.ts`); the view says what a paste of something else makes (the
 * board: pictures from the computer become image items, plain text a note).
 * On Linux a middle click pastes the last selected text: that one is ignored, as the middle
 * button pans the view.
 */

/** What the clipboard handlers need (one view's). */
export interface PasteDeps {
  /** Whether the view is shown and may be changed. */
  active(): boolean;
  /** The selection as clipboard text (`null`: nothing to copy). */
  copy(): string | null;
  /** Removes the selection (a cut). */
  remove(): void;
  /** Adds what clipboard text `text` holds, where the pointer is. */
  paste(text: string): void;
  /** Files from the clipboard (pictures); left out: the text is pasted instead. */
  pictures?(files: File[]): void;
}

/** Whether the keyboard is in a field (its own copy and paste must work). */
function typing(): boolean {
  const el = document.activeElement;
  return el instanceof HTMLElement && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);
}

/** How long after a middle click a paste is taken as the click's own (Linux's primary selection). */
export const MIDDLE_PASTE_MS = 500;

/** Whether a paste at `now` came from the middle click ended at `middleAt` (ms, `-Infinity`: none). */
export function pastedByMiddleClick(now: number, middleAt: number): boolean {
  return now - middleAt >= 0 && now - middleAt < MIDDLE_PASTE_MS;
}

/** Listens to the page's clipboard events; returns the function that stops. */
export function listenClipboard(deps: PasteDeps): () => void {
  /** When the middle button was last pressed or let go. */
  let middleAt = -Infinity;
  const middle = (e: MouseEvent) => {
    if (e.button === 1) middleAt = performance.now();
  };
  const copy = (e: ClipboardEvent, cut: boolean) => {
    if (!deps.active() || typing() || getSelection()?.toString()) return;
    const text = deps.copy();
    if (!text) return;
    e.preventDefault();
    e.clipboardData?.setData("text/plain", text);
    if (cut) deps.remove();
  };
  const paste = (e: ClipboardEvent) => {
    if (!deps.active() || typing()) return;
    if (pastedByMiddleClick(performance.now(), middleAt)) return void e.preventDefault();
    const files = [...(e.clipboardData?.files ?? [])];
    const text = e.clipboardData?.getData("text/plain") ?? "";
    e.preventDefault();
    if (files.length && deps.pictures) return deps.pictures(files);
    deps.paste(text);
  };
  const onCopy = (e: ClipboardEvent) => copy(e, false);
  const onCut = (e: ClipboardEvent) => copy(e, true);
  document.addEventListener("copy", onCopy);
  document.addEventListener("cut", onCut);
  document.addEventListener("paste", paste);
  document.addEventListener("mousedown", middle, true);
  document.addEventListener("mouseup", middle, true);
  return () => {
    document.removeEventListener("mousedown", middle, true);
    document.removeEventListener("mouseup", middle, true);
    document.removeEventListener("copy", onCopy);
    document.removeEventListener("cut", onCut);
    document.removeEventListener("paste", paste);
  };
}
